import { logger } from '@/utils/logging'
import type { VastAd, VastTrackingEvent } from './types'

/**
 * Fires tracking pixel URLs for a VAST ad event.
 * Uses `navigator.sendBeacon` for reliability (survives page unload),
 * falling back to `fetch` with keepalive.
 */
export function fireTrackingUrls(urls: string[]): void {
  for (const url of urls) {
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url)
      } else {
        fetch(url, { keepalive: true, mode: 'no-cors' }).catch((error) => {
          logger().warn('Ad tracking pixel fetch failed', { url }, error)
        })
      }
    } catch (error) {
      logger().warn('Ad tracking pixel failed', { url }, error)
    }
  }
}

/** Fire impression tracking URLs for a VAST ad. */
export function fireImpressions(ad: VastAd): void {
  fireTrackingUrls(ad.impressions)
  fireTrackingEvent(ad, 'creativeView')
}

/** Fire a named tracking event for a VAST ad. */
export function fireTrackingEvent(ad: VastAd, event: VastTrackingEvent): void {
  const urls = ad.trackingEvents[event]
  if (urls?.length) {
    fireTrackingUrls(urls)
  }
}

/** Fire click tracking URLs for a VAST ad. */
export function fireClickTracking(ad: VastAd): void {
  fireTrackingUrls(ad.clickTracking)
}

/**
 * Creates a quartile tracker that fires tracking events based on ad playback progress.
 * Call `update(currentTime, duration)` on each timeupdate to fire events at the correct moments.
 */
export function createQuartileTracker(ad: VastAd) {
  const fired = new Set<VastTrackingEvent>()

  return {
    update(currentTime: number, duration: number) {
      if (duration <= 0) return
      const progress = currentTime / duration

      if (!fired.has('start') && progress >= 0) {
        fired.add('start')
        fireTrackingEvent(ad, 'start')
      }
      if (!fired.has('firstQuartile') && progress >= 0.25) {
        fired.add('firstQuartile')
        fireTrackingEvent(ad, 'firstQuartile')
      }
      if (!fired.has('midpoint') && progress >= 0.5) {
        fired.add('midpoint')
        fireTrackingEvent(ad, 'midpoint')
      }
      if (!fired.has('thirdQuartile') && progress >= 0.75) {
        fired.add('thirdQuartile')
        fireTrackingEvent(ad, 'thirdQuartile')
      }
    },

    fireComplete() {
      if (!fired.has('complete')) {
        fired.add('complete')
        fireTrackingEvent(ad, 'complete')
      }
    },

    fireSkip() {
      if (!fired.has('skip')) {
        fired.add('skip')
        fireTrackingEvent(ad, 'skip')
      }
    },
  }
}

/**
 * Opens a click-through URL in a new tab and fires click tracking pixels.
 * Returns true if a click-through URL was opened.
 */
export function handleAdClick(ad: VastAd): boolean {
  fireClickTracking(ad)
  if (ad.clickThrough) {
    try {
      window.open(ad.clickThrough, '_blank', 'noopener,noreferrer')
    } catch (error) {
      logger().warn('Failed to open ad click-through', { url: ad.clickThrough }, error)
    }
    return true
  }
  return false
}
